import * as Sentry from '@sentry/react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { MotionConfig } from 'framer-motion';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';

import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { SENTRY_DSN } from './lib/config';
import { persistOptions } from './lib/persistedQueryClient';
import { queryClient } from './lib/queryClient';
import './styles/index.css';

// Error tracking (CP-P4-T04/DEC-0040) — no-op without a DSN (dev/test default).
if (SENTRY_DSN) {
  Sentry.init({ dsn: SENTRY_DSN, tracesSampleRate: 0.1 });
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

createRoot(container).render(
  <StrictMode>
    {/* Auto-respects prefers-reduced-motion across every framer-motion
     * animation in the app — was unhandled before this redesign. */}
    <MotionConfig reducedMotion="user">
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);

// Installable + offline (CP-P4-T02/DEC-0038). registerType 'autoUpdate' means
// no update-prompt UI is needed; registration failure must never block app
// boot, so it's fire-and-forget.
if ('serviceWorker' in navigator) {
  registerSW({ immediate: true });
}
